import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Wallet, Activity, UserPlus, Repeat, ArrowUpRight, ArrowDownRight, AlertTriangle, Clock, Cake, ChevronRight, Sparkles, TrendingUp, Trophy } from 'lucide-react';
import { useAuth } from '@techsofit/app-kit';
import { Button, Card, StatCard, EmptyState, Skeleton, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { QuickActions } from '../components/QuickActions';
import { MemberRow } from '../components/MemberRow';
import { CheckInLeaderboard } from '../components/CheckInLeaderboard';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import type { Alert, Insight } from '@techsofit/api-types';
import { getDashboard, getRevenueReport, getAlerts, getInsights } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { money, moneyCompact, formatDate, todayIso, gymHour } from '../lib/format';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: s, loading, error } = useAsync(getDashboard);
  // Real collections series (last 6 months) — same source as the Reports page.
  const { data: revenue, loading: revLoading } = useAsync(() => getRevenueReport({ months: 6 }));
  const [lbPeriod, setLbPeriod] = useState<'WEEK' | 'MONTH'>('WEEK');
  const firstName = (user?.fullName ?? '').split(' ')[0];
  const gymName = user?.gymName ?? 'your gym';
  const dateLabel = formatDate(todayIso(), { weekday: 'long', day: 'numeric', month: 'long' });
  const hour = gymHour();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const months = revenue?.byMonth ?? [];
  const peak = Math.max(1, ...months.map((m) => Number(m.amount)));
  const revenueTrend = trendFromMonths(months);
  const dayIso = todayIso();
  const monthStartIso = `${dayIso.slice(0, 7)}-01`;
  // Business-story trends: "up is good" for all four flow metrics.
  const revenueTodayTrend = s ? pctTrend(Number(s.revenueToday), Number(s.revenueYesterday)) : undefined;
  const checkinsTrend = s ? pctTrend(s.checkinsToday, s.checkinsYesterday) : undefined;
  const newMembersTrend = s ? pctTrend(s.newThisMonth, s.newLastMonthToDate) : undefined;
  const renewalsTrend = s ? pctTrend(s.renewalsThisMonth, s.renewalsLastMonthToDate) : undefined;

  if (loading) {
    return (
      <section className="animate-fade-up">
        <Skeleton className="h-10 w-64" />
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="mt-3 h-48" />
      </section>
    );
  }

  if (error || !s) {
    return (
      <section className="animate-fade-up">
        <EmptyState title="Couldn’t load the dashboard" description={error ?? 'Please refresh.'} />
      </section>
    );
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={dateLabel}
        title={`${greeting}, ${firstName}`}
        description={`Here's where ${gymName} stands today. Anyone expiring or lapsed is one tap from a WhatsApp nudge.`}
        actions={<QuickActions variant="button" className="hidden md:inline-flex" />}
      />

      <SubscriptionBanner className="mb-4" />

      <NeedsAttention />


      {/* Business story — today's money & activity, this month's growth */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Today's revenue"
          value={money(s.revenueToday)}
          sub={`${money(s.revenueYesterday)} yesterday`}
          icon={<Wallet className="size-4" aria-hidden />}
          trend={revenueTodayTrend}
          actionLabel="See today's payments"
          onClick={() => navigate(`/payments?from=${dayIso}&to=${dayIso}`)}
        />
        <StatCard
          label="Today's check-ins"
          value={s.checkinsToday}
          sub={`${s.checkinsYesterday} yesterday`}
          icon={<Activity className="size-4" aria-hidden />}
          trend={checkinsTrend}
          actionLabel="See who's in"
          onClick={() => navigate('/attendance')}
        />
        <StatCard
          label="New members"
          value={s.newThisMonth}
          sub="joined this month"
          icon={<UserPlus className="size-4" aria-hidden />}
          trend={newMembersTrend}
          actionLabel="See new members"
          onClick={() => navigate('/members?filter=new')}
        />
        <StatCard
          label="Renewals"
          value={s.renewalsThisMonth}
          sub="this month"
          icon={<Repeat className="size-4" aria-hidden />}
          trend={renewalsTrend}
          actionLabel="See renewals"
          onClick={() => navigate(`/payments?type=RENEWAL&from=${monthStartIso}&to=${dayIso}`)}
        />
      </div>

      {/* Revenue strip + new members */}
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink">Revenue this month</p>
              <p className="font-display text-2xl font-semibold tabular text-ink">{money(s.revenueThisMonth)}</p>
            </div>
            {revenueTrend && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-xs font-medium',
                  revenueTrend.direction === 'down' ? 'bg-bad-soft text-bad-ink' : 'bg-surface text-ink-soft',
                )}
              >
                {revenueTrend.direction === 'down' ? (
                  <ArrowDownRight className="size-3.5 text-bad" aria-hidden />
                ) : (
                  <ArrowUpRight className="size-3.5 text-ok" aria-hidden />
                )}
                {revenueTrend.direction === 'up' ? 'trending up' : revenueTrend.direction === 'down' ? 'trending down' : 'steady'}
              </span>
            )}
          </div>

          {revLoading ? (
            <Skeleton className="mt-6 h-32" />
          ) : months.length ? (
            <>
              <p className="mt-4 text-xs text-muted">Last 6 months</p>
              <div className="mt-2 flex h-40 items-stretch gap-2.5">
                {months.map((m, i) => {
                  const last = i === months.length - 1;
                  const amt = Number(m.amount);
                  // Leave headroom above the tallest bar so its value label never clips.
                  const h = amt > 0 ? Math.max(4, Math.round((amt / peak) * 82)) : 0;
                  return (
                    <div key={m.month} className="flex flex-1 flex-col justify-end gap-1.5">
                      <span className={cn('text-center text-[11px] tabular', last ? 'font-semibold text-ink' : 'text-muted')}>
                        {amt > 0 ? moneyCompact(amt) : '—'}
                      </span>
                      <div
                        className={cn('w-full rounded-t-lg rounded-b-md transition-all hover:opacity-90', last ? 'bg-accent' : 'bg-dark')}
                        style={{ height: `${h}%` }}
                        title={`${money(m.amount)} · ${monthLabel(m.month)}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex gap-2.5">
                {months.map((m) => (
                  <span key={m.month} className="flex-1 text-center text-xs text-muted">
                    {monthLabel(m.month)}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <p className="mt-6 flex h-32 items-center justify-center text-sm text-muted">No collections to chart yet.</p>
          )}
        </Card>

        <Card tone="dark" className="flex flex-col justify-between p-5">
          <div>
            <div className="flex items-center gap-2 text-on-dark">
              <Users className="size-4" aria-hidden />
              <p className="text-sm font-medium">Active members</p>
            </div>
            <p className="mt-4 font-display text-metric tabular text-on-dark">{s.activeCount}</p>
            <p className="mt-1 text-sm text-on-dark-muted">
              paying &amp; current at {gymName}
              {s.expiringCount > 0 && ` · ${s.expiringCount} expiring soon`}
            </p>
          </div>
          <Button variant="primary" block className="mt-6" onClick={() => navigate('/members')}>
            View all members
          </Button>
        </Card>
      </div>

      <InsightsPanel />

      {/* Action lists */}
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <Card className="p-5">
          <header className="mb-2 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold text-ink">Expiring this week</h2>
              <p className="text-xs text-muted">Next 7 days</p>
            </div>
            <span className="rounded-pill bg-accent-soft px-2.5 py-1 text-xs font-medium text-ink">{s.expiringCount}</span>
          </header>
          {s.expiringSoon.length ? (
            <div className="-mx-2 flex flex-col">
              {s.expiringSoon.map((m) => (
                <MemberRow key={m.id} member={m} showNudge />
              ))}
            </div>
          ) : (
            <EmptyState title="Nobody's expiring" description="Every active member is comfortably in date." className="bg-surface" />
          )}
        </Card>

        <Card className="p-5">
          <header className="mb-2 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold text-ink">Recently lapsed</h2>
              <p className="text-xs text-muted">Last 14 days</p>
            </div>
            <span className="rounded-pill bg-bad-soft px-2.5 py-1 text-xs font-medium text-bad-ink">{s.lapsedCount}</span>
          </header>
          {s.recentlyLapsed.length ? (
            <div className="-mx-2 flex flex-col">
              {s.recentlyLapsed.map((m) => (
                <MemberRow key={m.id} member={m} showNudge />
              ))}
            </div>
          ) : (
            <EmptyState title="No recent lapses" description="Nobody has fallen out of date in the last 14 days." className="bg-surface" />
          )}
        </Card>
      </div>

      <Card className="mt-3 p-5">
        <header className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Trophy className="size-4 text-ink" aria-hidden />
            <h2 className="font-display text-lg font-semibold text-ink">Check-in leaderboard</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/attendance?tab=leaderboard')}>
            See all
          </Button>
        </header>
        <CheckInLeaderboard
          rows={lbPeriod === 'WEEK' ? (s.weekCheckIns ?? []) : (s.monthCheckIns ?? [])}
          period={lbPeriod}
          onPeriod={setLbPeriod}
        />
      </Card>
    </section>
  );
}

/** A percentage trend pill comparing a flow metric to its prior period. "Up is good" for all
 *  four dashboard flow metrics (revenue, check-ins, sign-ups, renewals), so `good: true`. */
function pctTrend(cur: number, prev: number): { value: string; direction: 'up' | 'down' | 'flat'; good: boolean } | undefined {
  if (prev <= 0) return cur > 0 ? { value: 'new', direction: 'up', good: true } : undefined;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return { value: '0%', direction: 'flat', good: true };
  return { value: `${Math.abs(pct)}%`, direction: pct > 0 ? 'up' : 'down', good: true };
}

/** Month-over-month trend from the tail of the monthly revenue series. */
function trendFromMonths(months: { amount: string }[]): { value: string; direction: 'up' | 'down' | 'flat' } | undefined {
  if (months.length < 2) return undefined;
  const prev = Number(months[months.length - 2].amount);
  const cur = Number(months[months.length - 1].amount);
  if (!(prev > 0)) return undefined;
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { value: `${Math.abs(pct)}%`, direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' });

const ALERT_META: Record<string, { icon: typeof AlertTriangle; tone: 'bad' | 'warn' | 'info'; to?: string }> = {
  LIKELY_TO_CANCEL: { icon: AlertTriangle, tone: 'bad', to: '/health' },
  EXPIRING: { icon: Clock, tone: 'warn', to: '/members?filter=expiring' },
  AT_RISK: { icon: Activity, tone: 'warn', to: '/health' },
  BIRTHDAYS: { icon: Cake, tone: 'info' },
};

const alertTone = (tone: 'bad' | 'warn' | 'info') =>
  tone === 'bad' ? 'bg-bad-soft text-bad-ink' : tone === 'warn' ? 'bg-warn-soft text-warn-ink' : 'bg-accent-soft text-ink';

/** Proactive "needs attention" feed — at-risk members, expiries and birthdays. Hidden when all clear. */
function NeedsAttention() {
  const navigate = useNavigate();
  const { data } = useAsync(getAlerts);
  const alerts = data?.alerts ?? [];
  if (alerts.length === 0) return null;
  return (
    <Card className="mb-4 p-4">
      <p className="mb-2.5 text-sm font-medium text-ink">Needs attention</p>
      <ul className="flex flex-col gap-1.5">
        {alerts.map((a) => (
          <AlertRow key={a.kind} alert={a} onOpen={() => ALERT_META[a.kind]?.to && navigate(ALERT_META[a.kind].to!)} />
        ))}
      </ul>
    </Card>
  );
}

function AlertRow({ alert, onOpen }: { alert: Alert; onOpen: () => void }) {
  const meta = ALERT_META[alert.kind] ?? { icon: AlertTriangle, tone: 'info' as const };
  const Icon = meta.icon;
  const clickable = !!meta.to;
  const inner = (
    <div className="flex items-center gap-3">
      <span className={cn('grid size-8 shrink-0 place-items-center rounded-control', alertTone(meta.tone))}>
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{alert.title}</p>
        {alert.names.length > 0 && <p className="truncate text-xs text-muted">{alert.names.join(', ')}</p>}
      </div>
      {clickable && <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />}
    </div>
  );
  return (
    <li>
      {clickable ? (
        <button type="button" onClick={onOpen} className="w-full rounded-control px-1 py-1 text-left transition-colors hover:bg-surface">
          {inner}
        </button>
      ) : (
        <div className="px-1 py-1">{inner}</div>
      )}
    </li>
  );
}

const INSIGHT_TONE: Record<Insight['tone'], { dot: string; icon: typeof TrendingUp }> = {
  positive: { dot: 'text-ok', icon: TrendingUp },
  warning: { dot: 'text-warn', icon: AlertTriangle },
  neutral: { dot: 'text-muted', icon: Activity },
};

/** Deterministic plain-language observations ("Revenue up 12%…", "Mondays busiest"). Hidden when none.
 *  Insights that reference people are clickable — they drill into the list where you can nudge them. */
function InsightsPanel() {
  const navigate = useNavigate();
  const { data } = useAsync(getInsights);
  const insights = data?.insights ?? [];
  if (insights.length === 0) return null;
  return (
    <Card className="mt-3 p-5">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="size-4 text-accent-ink" aria-hidden />
        <h2 className="font-display text-lg font-semibold text-ink">Insights</h2>
      </div>
      <ul className="flex flex-col gap-1">
        {insights.map((ins, i) => {
          const tone = INSIGHT_TONE[ins.tone] ?? INSIGHT_TONE.neutral;
          const Icon = tone.icon;
          const body = (
            <>
              <Icon className={cn('mt-0.5 size-4 shrink-0', tone.dot)} aria-hidden />
              <span className="flex-1 text-ink-soft">{ins.text}</span>
              {ins.link && <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />}
            </>
          );
          return (
            <li key={i}>
              {ins.link ? (
                <button
                  type="button"
                  onClick={() => navigate(ins.link!)}
                  className="flex w-full items-start gap-2.5 rounded-control px-1.5 py-1.5 text-left text-sm transition-colors hover:bg-surface"
                >
                  {body}
                </button>
              ) : (
                <div className="flex items-start gap-2.5 px-1.5 py-1.5 text-sm">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
